/* eslint-disable react/no-unescaped-entities */
import { Link } from 'react-router-dom';
import {
  Home, MapPin, Users, Scale, Coins, Stamp, FileText, Calendar,
} from 'lucide-react';
import GuidePage from '@/components/library/GuidePage';

const config = {
  path: '/attestation-immobiliere',
  slug: 'attestation-immobiliere',
  breadcrumbLabel: 'Attestation immobilière',
  readingTime: '7 min',
  lastUpdated: '18 février 2026',
  seoTitle: 'Attestation immobilière après décès : guide | HÉRITA',
  seoDescription:
    "Comprenez facilement une attestation immobilière après décès : à quoi elle sert, qui devient propriétaire, dans quelle proportion. Analysez votre document avec HÉRITA.",

  hero: {
    eyebrow: 'Guides HÉRITA',
    title: 'Comprendre une attestation immobilière',
    subtitle:
      "L'attestation immobilière acte officiellement le transfert d'un bien immobilier après un décès. HÉRITA vous aide à comprendre qui devient propriétaire, dans quelle proportion et avec quelles obligations.",
  },

  definition: {
    eyebrow: 'Définition',
    title: "Qu'est-ce qu'une attestation immobilière\u00A0?",
    paragraphs: [
      <>
        L'attestation immobilière — souvent appelée <em>attestation de propriété</em>
        {' '}ou <em>attestation après décès</em> — est un acte authentique signé
        chez le notaire qui constate officiellement{' '}
        <strong className="font-semibold text-[#1A2530]">le transfert d'un bien immobilier</strong>{' '}
        du patrimoine d'un défunt vers celui de ses héritiers.
      </>,
      <>
        Sans elle, l'héritier reste juridiquement invisible vis-à-vis du
        bien&nbsp;: il ne peut ni le vendre, ni le louer, ni l'hypothéquer,
        ni même le déclarer aux impôts. L'attestation comble cette absence en
        formalisant le changement de propriétaire au <em>service de la
        publicité foncière</em>.
      </>,
      <>
        <strong className="font-semibold text-[#1A2530]">Quand est-elle nécessaire&nbsp;?</strong>{' '}
        Dès qu'un bien immobilier figure dans la succession. Même un studio,
        même une part de bien en indivision&nbsp;: tout immeuble transmis doit
        faire l'objet d'une attestation. Elle est généralement préparée par
        le notaire dans les mois qui suivent l'acte de notoriété.
      </>,
      <>
        <strong className="font-semibold text-[#1A2530]">Que dit-elle&nbsp;?</strong>{' '}
        Elle identifie le défunt, ses héritiers, le bien transmis (avec son
        adresse précise et sa référence cadastrale), la quote-part recueillie
        par chacun, et la valeur retenue au jour du décès. Tout ce qui sera
        nécessaire à la future fiscalité — plus-value en cas de revente,
        droits de succession — est consigné ici.
      </>,
      <>
        Elle est ensuite publiée au service de la publicité foncière, ce qui
        rend le transfert opposable à tous. C'est à compter de cette
        publication que l'héritier peut, juridiquement et fiscalement, agir
        sur le bien.
      </>,
      <>
        Comprendre une attestation immobilière, c'est comprendre exactement
        ce que vous possédez désormais. HÉRITA vous le restitue clairement,
        et fait le lien avec votre future{' '}
        <Link to="/declaration-succession" className="text-[#1A2530] font-medium underline underline-offset-[5px] decoration-[#D3CCBE] hover:decoration-[#1A2530]">déclaration de succession</Link>.
      </>,
    ],
  },

  usage: {
    eyebrow: 'Utilité',
    title: "À quoi sert-elle\u00A0?",
    items: [
      { title: 'Officialiser le transfert', description: "L'attestation fait passer le bien immobilier du nom du défunt au nom des héritiers, au regard de la loi et du fisc." },
      { title: 'Rendre la vente possible', description: "Sans attestation publiée, aucune vente, aucun don, aucune hypothèque n'est possible. C'est elle qui débloque tout." },
      { title: 'Établir la valeur fiscale', description: "La valeur retenue dans l'acte sera la base de calcul des futures plus-values en cas de revente." },
      { title: 'Identifier la quote-part', description: "Chaque héritier sait exactement quelle fraction du bien lui appartient, en pleine propriété ou en démembrement." },
    ],
  },

  content: {
    eyebrow: 'Contenu',
    title: "Que contient une attestation immobilière\u00A0?",
    intro: "Huit éléments composent l'attestation. Leur précision conditionne la validité du transfert et toutes les démarches qui suivront.",
    cards: [
      { Icon: FileText, title: 'Défunt', description: "Identité complète, date et lieu de décès, dernier domicile et état civil détaillé." },
      { Icon: Users, title: 'Héritiers', description: "Liste précise avec état civil, lien de parenté et qualité d'héritier confirmée par l'acte de notoriété." },
      { Icon: Home, title: 'Description du bien', description: "Adresse complète, nature du bien (maison, appartement, terrain), surface, étage, lots de copropriété." },
      { Icon: MapPin, title: 'Références cadastrales', description: "Section, numéro de parcelle, et lieu-dit. Indispensables pour la publication au service de la publicité foncière." },
      { Icon: Scale, title: 'Quote-part recueillie', description: "Fraction du bien attribuée à chaque héritier : pleine propriété, nue-propriété, usufruit, en indivision." },
      { Icon: Coins, title: 'Valeur retenue', description: "Évaluation du bien au jour du décès. Cette valeur sert de base aux droits et à la future plus-value." },
      { Icon: Calendar, title: 'Origine de propriété', description: "Historique des transmissions précédentes (vente, donation, succession antérieure) sur 30 ans minimum." },
      { Icon: Stamp, title: 'Mentions notariales', description: "Notaire instrumentaire, numéro de l'acte, références de publication au service de la publicité foncière." },
    ],
  },

  checks: {
    eyebrow: 'Vigilance',
    title: 'Les points importants à vérifier',
    intro: "Une attestation immobilière conditionne toutes les démarches futures sur le bien. Voici ce qu'il faut relire avec soin.",
    items: [
      "S'assurer que la quote-part attribuée correspond bien à votre part dans la succession.",
      "Vérifier que l'adresse et les références cadastrales sont exactes — une erreur peut bloquer une future vente.",
      "Identifier votre statut : pleine propriété, nue-propriété ou indivision avec d'autres héritiers.",
      "Comprendre la valeur retenue : elle servira à calculer les plus-values en cas de revente.",
      "Vérifier l'origine de propriété sur 30 ans : aucune transmission ne doit manquer.",
      "S'assurer que l'attestation a bien été publiée au service de la publicité foncière.",
    ],
  },

  autoChecks: {
    eyebrow: 'Sans rien demander',
    title: 'Ce que HÉRITA identifie automatiquement',
    intro: "Dès l'import, HÉRITA repère le bien, sa valeur, votre quote-part et les démarches à effectuer.",
    items: [
      'Bien immobilier précisément identifié',
      'Héritiers et quote-part de chacun',
      'Type de propriété (pleine, nue, usufruit)',
      'Valeur retenue au jour du décès',
      'Origine de propriété et historique',
      'Publication au service de la publicité foncière',
      'Démarches restant à effectuer',
      'Questions à poser à votre notaire',
    ],
  },

  example: {
    eyebrow: 'Avant · Après',
    title: 'Un exemple simplifié',
    intro: "À gauche, un extrait fictif d'attestation immobilière. À droite, ce qu'HÉRITA en aurait expliqué.",
    extract: (
      <>
        «&nbsp;Aux termes d'un acte de notoriété, Madame Léa DURAND et
        Mademoiselle Camille DURAND ont été reconnues comme seules héritières
        de Monsieur Pierre DURAND. Il leur est ainsi transmis l'immeuble sis
        12 rue des Lilas à Bordeaux (33000), cadastré section AB n° 245,
        d'une surface de 142 m², évalué à la somme de CINQ CENT VINGT MILLE
        EUROS (520 000 €), à recueillir en usufruit total pour la veuve et en
        nue-propriété pour la fille unique.&nbsp;»
      </>
    ),
    headline: "Ce que cela signifie pour Camille",
    bullets: [
      { strong: 'Le bien concerné', rest: ' : la maison familiale au 12 rue des Lilas à Bordeaux, 142 m², parcelle AB 245.' },
      { strong: 'Vous recevez la nue-propriété', rest: ' de l\'intégralité du bien, évalué à 520 000 €.' },
      { strong: 'Votre mère conserve l\'usufruit total', rest: ' : elle peut continuer à habiter la maison ou la louer.' },
      { strong: 'Vous ne pouvez ni vendre ni louer', rest: ' seule : son accord d\'usufruitière est nécessaire.' },
      { strong: 'Au décès de votre mère', rest: ', vous deviendrez automatiquement pleine propriétaire, sans nouveau droit à payer.' },
    ],
  },

  faq: [
    { q: "Qu'est-ce qu'une attestation immobilière ?", a: "C'est un acte notarié qui constate officiellement le transfert d'un bien immobilier du défunt vers ses héritiers. Publiée au service de la publicité foncière, elle rend le changement de propriétaire opposable à tous." },
    { q: "Est-elle obligatoire ?", a: "Oui, dès qu'un bien immobilier figure dans la succession. Sans attestation, l'héritier ne peut ni vendre, ni louer, ni hypothéquer le bien. Elle est en pratique exigée par toutes les administrations." },
    { q: "Quand est-elle établie ?", a: "Généralement dans les mois qui suivent l'acte de notoriété, une fois les héritiers identifiés et la déclaration de succession en cours. Elle doit être publiée dans les 4 mois suivant son établissement." },
    { q: "Combien coûte-t-elle ?", a: "Le coût se compose des émoluments du notaire (encadrés par décret), des frais de publication au service de la publicité foncière (environ 0,1 % de la valeur du bien) et des frais administratifs. Comptez entre 1 et 2 % de la valeur du bien." },
    { q: "Qui doit la signer ?", a: "Tous les héritiers qui recueillent le bien doivent signer l'acte, en pleine propriété, en nue-propriété ou en usufruit. La signature peut se faire en présentiel chez le notaire ou par procuration." },
    { q: "Comment HÉRITA l'analyse-t-il ?", a: "HÉRITA identifie le bien, son adresse, sa surface, sa valeur, votre quote-part, votre statut (pleine propriété, nue-propriété, usufruit) et les éventuelles obligations associées. Vous recevez en quelques minutes une lecture claire et personnalisée." },
    { q: "Que se passe-t-il si on ne fait pas l'attestation ?", a: "Le bien reste juridiquement dans le patrimoine du défunt. Aucune vente n'est possible. L'administration fiscale peut également réclamer des pénalités si la déclaration de succession a été déposée mais non suivie de la publication foncière." },
    { q: "Quelle est la différence avec un acte de vente ?", a: "L'attestation immobilière constate un transfert par succession (sans contrepartie financière). L'acte de vente, lui, constate une cession contre paiement d'un prix. Les deux sont publiés au service de la publicité foncière, mais les régimes fiscaux diffèrent." },
    { q: "Peut-on vendre le bien immédiatement ?", a: "Oui, dès la publication de l'attestation. Si plusieurs héritiers sont propriétaires en indivision, l'accord de tous est nécessaire pour vendre. Si un seul héritier a recueilli le bien, il peut le vendre librement." },
    { q: "Quel rapport avec la déclaration de succession ?", a: "Les deux sont complémentaires. La déclaration de succession est fiscale et calcule les droits dus. L'attestation immobilière est patrimoniale et transfère officiellement la propriété. La valeur retenue est en principe identique dans les deux actes." },
  ],

  finalCta: {
    title: "Vous avez reçu une attestation immobilière\u00A0?",
    subtitle: "Déposez votre document. HÉRITA identifie automatiquement le bien transmis, votre quote-part, votre statut de propriété et les démarches restantes afin de vous fournir une lecture claire et personnalisée.",
  },
};

const AttestationImmobiliere = () => <GuidePage config={config} />;
export default AttestationImmobiliere;
